import { makeAutoObservable } from "mobx";
import { BreadcrumbItem } from "src/appStore";
import { loadCatalog } from "src/business/catalog/apiCatalog";
import { ZCatalog } from "src/business/catalog/ZCatalog";
import { loadWorkflow } from "src/business/workflow/apiWorkflow";
import { getWorkflowDisplayName } from "src/business/workflow/getWorkflowDisplayName";
import { getWorkflowStepDisplayName } from "src/business/workflow/getWorkflowStepDisplayName";
import { ZWorkflow } from "src/business/workflow/ZWorkflow";
import { RemoteData } from "src/common/RemoteData";
import { CDSFormStore } from "src/pages/catalogs/CatalogRecordForm/CDSFormStore";
import {
  createWorkflowRecordFormLoader,
  WorkflowRecordFormContext,
} from "src/pages/catalogs/CatalogRecordForm/createWorkflowRecordFormLoader";
import { PageUrl } from "src/routes/PageUrl";

export type WfNewRecordData = {
  catalog: ZCatalog;
  workflow: ZWorkflow;
};

export const createWfNewRecordStore = () =>
  makeAutoObservable({
    async init(catalogId: string, workflowId: string) {
      this.setCatalogId(catalogId);
      this.setWorkflowId(workflowId);
      try {
        this.setData({ status: "wait" });
        const [catalog, workflow] = await Promise.all([
          loadCatalog(catalogId),
          loadWorkflow(workflowId),
        ]);
        if (workflow.catalog?.id !== catalogId) {
          throw Error("Workflow не соответствует выбранному каталогу");
        }
        const initialStep = workflow.steps?.[0];
        if (!initialStep) {
          throw Error("Не найден стартовый шаг workflow");
        }
        this.setData({ status: "ready", result: { catalog, workflow } });
        this.setCDSFormStore(
          new CDSFormStore(createWorkflowRecordFormLoader(catalog), {
            step: initialStep,
          }),
        );
      } catch (error) {
        this.setData({ status: "error", error });
      }
    },
    catalogId: "",
    setCatalogId(id: string) {
      this.catalogId = id;
    },
    workflowId: "",
    setWorkflowId(id: string) {
      this.workflowId = id;
    },
    data: { status: "none" } as RemoteData<WfNewRecordData>,
    setData(newData: RemoteData<WfNewRecordData>) {
      this.data = newData;
    },
    cdsFormStore: null as CDSFormStore<WorkflowRecordFormContext> | null,
    setCDSFormStore(store: CDSFormStore<WorkflowRecordFormContext>) {
      this.cdsFormStore = store;
    },
    get breadcrumbs(): BreadcrumbItem[] {
      const res: BreadcrumbItem[] = [
        { title: "Рабочие потоки и версии", page: PageUrl.workflowStreams },
      ];
      if (this.data.status === "ready") {
        res.push({
          title: getWorkflowDisplayName(this.data.result.workflow),
          page: PageUrl.workflowCards,
          params: { id: this.data.result.workflow.id },
        });
      }
      res.push({ title: "Новая рабочая версия" });
      return res;
    },
    get currentStepName(): string | undefined {
      const { data } = this;
      if (data.status !== "ready") return undefined;
      const step = data.result.workflow.steps?.[0];
      return getWorkflowStepDisplayName(step) || undefined;
    },
    get currentStepDescription(): string | null | undefined {
      const { data } = this;
      if (data.status !== "ready") return undefined;
      return data.result.workflow.steps?.[0]?.description;
    },
  });

export type WfNewRecordStore = ReturnType<typeof createWfNewRecordStore>;
